import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AppShell, Button, Card, EmptyState, Icon, MoneyInput, ProgressBar, Skeleton, StatCard } from '../../ui';
import { usePerfilContexto } from '../../contextos/PerfilContexto';
import { useCategorias } from '../../servicos/categorias/useCategorias';
import { useTransacoes } from '../../servicos/transacoes/useTransacoes';
import { Categoria } from '../../servicos/categorias/typeCategoria';
import { Transacao } from '../../servicos/transacoes/typeTransacao';
import { useAtualizacaoContexto } from '../../contextos/AtualizacaoContexto';
import { valorParaTexto } from '../../utils/Utils';
import {
  numero,
  progressoDaCategoria,
  realizadoDe,
  resumoDoOrcamento,
  somarPorCategoria,
  sugestaoDeMeta
} from './logica';
import styles from './Metas.module.css';

/**
 * Metas — orçamento mensal por categoria. Responde: "estou dentro do orçamento
 * este mês?" com projeção de ritmo e sugestão inteligente de meta (média dos
 * últimos 3 meses) para categorias que ainda não têm meta definida.
 */
export default function Metas() {
  const hoje = new Date();
  const [mes, setMes] = useState<Date>(new Date(hoje.getFullYear(), hoje.getMonth(), 1));

  const { categorias, updateCategoria, loading: carregandoCategorias } = useCategorias();
  const { perfil } = usePerfilContexto();
  // Primeira carga de categorias ainda no ar: skeleton em vez de EmptyState.
  const carregandoLista = categorias.length === 0 && (carregandoCategorias || !!perfil.carregarCategorias);
  const { atualizar, setAtualizar } = useAtualizacaoContexto();
  const navigate = useNavigate();

  // Drill-down: histórico da categoria no mês exibido, já filtrado nas Movimentações.
  const verLancamentos = (categoria: Categoria) => {
    const paraISO = (data: Date) =>
      `${data.getFullYear()}-${String(data.getMonth() + 1).padStart(2, '0')}-${String(data.getDate()).padStart(2, '0')}`;
    navigate('/movimentacoes', {
      state: {
        categoriaIds: [categoria.id?.toString() ?? ''],
        dataInicial: paraISO(mes),
        dataFinal: paraISO(new Date(mes.getFullYear(), mes.getMonth() + 1, 0))
      }
    });
  };

  // Duas instâncias independentes (loading próprio) para o mês e para o histórico.
  const { fetchTransacoes: fetchMes } = useTransacoes(false);
  const { fetchTransacoes: fetchHistorico } = useTransacoes(false);

  const [transacoesMes, setTransacoesMes] = useState<Transacao[]>([]);
  const [historico, setHistorico] = useState<Transacao[]>([]);

  const [editandoId, setEditandoId] = useState<number | null>(null);
  const [valorEdicao, setValorEdicao] = useState<number>(0);
  const [salvandoId, setSalvandoId] = useState<number | null>(null);

  // Transações do mês selecionado.
  useEffect(() => {
    let ativo = true;
    (async () => {
      const inicio = new Date(mes.getFullYear(), mes.getMonth(), 1);
      const fim = new Date(mes.getFullYear(), mes.getMonth() + 1, 0);
      const colecao = await fetchMes({ dataInicial: inicio, dataFinal: fim });
      if (ativo) setTransacoesMes(colecao || []);
    })();
    return () => {
      ativo = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mes, atualizar]);

  // Histórico dos 3 meses anteriores ao selecionado — base da sugestão de meta.
  useEffect(() => {
    let ativo = true;
    (async () => {
      const inicio = new Date(mes.getFullYear(), mes.getMonth() - 3, 1);
      const fim = new Date(mes.getFullYear(), mes.getMonth(), 0);
      const colecao = await fetchHistorico({ dataInicial: inicio, dataFinal: fim });
      if (ativo) setHistorico(colecao || []);
    })();
    return () => {
      ativo = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mes, atualizar]);

  const rotuloMes = `${mes.toLocaleDateString('pt-BR', { month: 'long' })} ${mes.getFullYear()}`;
  const passoMes = (delta: number) => {
    setEditandoId(null);
    setMes((atual) => new Date(atual.getFullYear(), atual.getMonth() + delta, 1));
  };

  // Recortes de categorias.
  const categoriasDespesa = categorias.filter((categoria) => categoria.tipo === 'D');
  const despesasComMeta = categoriasDespesa.filter((categoria) => numero(categoria.valorMeta) > 0);
  const despesasSemMeta = categoriasDespesa.filter((categoria) => numero(categoria.valorMeta) <= 0);
  const receitasComMeta = categorias.filter(
    (categoria) => categoria.tipo === 'R' && numero(categoria.valorMeta) > 0
  );

  // Realizado do mês por categoria.
  const gastoMes = somarPorCategoria(transacoesMes, 'D');
  const receitaMes = somarPorCategoria(transacoesMes, 'R');
  const gastoHistorico = somarPorCategoria(historico, 'D');

  /** Média mensal dos últimos 3 meses, arredondada pra cima em múltiplos de R$10. */
  const sugestaoPara = (categoria: Categoria): number => sugestaoDeMeta(categoria, gastoHistorico);

  // Visão geral (StatCards) + ritmo: projeção de fim de mês para o mês atual.
  const { orcamentoTotal, gastoOrcado, disponivel, pctOrcamento, ehMesAtual, ehMesFuturo, projecao } =
    resumoDoOrcamento(despesasComMeta, gastoMes, mes, hoje);

  const iniciarEdicao = (categoria: Categoria) => {
    if (categoria.id === null || categoria.id === undefined) return;
    setEditandoId(categoria.id);
    setValorEdicao(numero(categoria.valorMeta) || sugestaoPara(categoria));
  };

  const salvarMeta = async (categoria: Categoria, valor: number) => {
    if (categoria.id === null || categoria.id === undefined || valor <= 0) return;
    setSalvandoId(categoria.id);
    await updateCategoria(categoria.id, { ...categoria, valorMeta: valor });
    setSalvandoId(null);
    setEditandoId(null);
    setAtualizar((atual) => !atual);
  };

  /** Editor inline da meta (MoneyInput + confirmar/cancelar) ou botão lápis. */
  const renderAcaoMeta = (categoria: Categoria) => {
    if (editandoId === categoria.id) {
      return (
        <div className={styles.edicao}>
          <div className={styles.campoEdicao}>
            <MoneyInput
              valor={valorEdicao}
              onChange={setValorEdicao}
              autoFocus
              onEnter={() => salvarMeta(categoria, valorEdicao)}
            />
          </div>
          <Button
            variante="primaria"
            tamanho="sm"
            icone="check"
            carregando={salvandoId === categoria.id}
            onClick={() => salvarMeta(categoria, valorEdicao)}
          />
          <Button variante="fantasma" tamanho="sm" icone="close" onClick={() => setEditandoId(null)} />
        </div>
      );
    }
    return <Button variante="fantasma" tamanho="sm" icone="edit" onClick={() => iniciarEdicao(categoria)} />;
  };

  /** Linha de categoria COM meta: progresso, restante/excedente e edição inline. */
  const renderLinhaComMeta = (categoria: Categoria, tipo: 'D' | 'R') => {
    const { meta, realizado, percentual, diferenca } = progressoDaCategoria(
      categoria,
      tipo === 'D' ? gastoMes : receitaMes
    );

    let legenda: string;
    let classeLegenda = styles.legenda;
    if (tipo === 'D') {
      if (diferenca >= 0) {
        legenda = `Restam ${valorParaTexto(diferenca, true)}`;
      } else {
        legenda = `${valorParaTexto(-diferenca, true)} acima da meta`;
        classeLegenda = `${styles.legenda} ${styles.legendaNegativa}`;
      }
    } else if (diferenca > 0) {
      legenda = `Faltam ${valorParaTexto(diferenca, true)} para a meta`;
    } else {
      legenda = `Meta alcançada${diferenca < 0 ? `, ${valorParaTexto(-diferenca, true)} além` : ''}`;
      classeLegenda = `${styles.legenda} ${styles.legendaPositiva}`;
    }

    return (
      <Card key={categoria.id} className={styles.linhaMeta}>
        <div
          className={styles.iconeCategoria}
          style={{ background: `${categoria.corIdentificacao}22`, color: categoria.corIdentificacao }}
        >
          <Icon nome={categoria.icone} tamanho={20} />
        </div>
        <div className={styles.infoMeta}>
          <div className={styles.topoLinha}>
            <span className={styles.nomeCategoria}>{categoria.nome}</span>
            <span className={styles.valores}>
              {valorParaTexto(realizado, true)} de {valorParaTexto(meta, true)}
            </span>
          </div>
          <ProgressBar percentual={percentual} tom={tipo === 'D' ? 'auto-despesa' : 'auto-receita'} />
          <span className={classeLegenda}>{legenda}</span>
        </div>
        <div className={styles.acaoMeta}>
          <Button
            variante="fantasma"
            tamanho="sm"
            icone="receipt_long"
            onClick={() => verLancamentos(categoria)}
            title="Ver os lançamentos desta categoria no mês"
          />
          {renderAcaoMeta(categoria)}
        </div>
      </Card>
    );
  };

  /** Linha de categoria SEM meta: gasto do mês, sugestão e definição rápida. */
  const renderLinhaSemMeta = (categoria: Categoria) => {
    const gasto = realizadoDe(categoria, gastoMes);
    const sugestao = sugestaoPara(categoria);

    return (
      <Card key={categoria.id} className={styles.linhaMeta}>
        <div
          className={styles.iconeCategoria}
          style={{ background: `${categoria.corIdentificacao}22`, color: categoria.corIdentificacao }}
        >
          <Icon nome={categoria.icone} tamanho={20} />
        </div>
        <div className={styles.infoMeta}>
          <span className={styles.nomeCategoria}>{categoria.nome}</span>
          {sugestao > 0 ? (
            <span className={styles.sugestao}>
              Sugestão de meta: {valorParaTexto(sugestao, true)} (média dos últimos 3 meses)
            </span>
          ) : (
            <span className={styles.legenda}>Sem histórico recente para sugerir uma meta.</span>
          )}
        </div>
        {/* Coluna própria, centralizada na vertical do card (o card é flex center). */}
        <span className={styles.valores}>Gasto no mês: {valorParaTexto(gasto, true)}</span>
        <div className={styles.acaoMeta}>
          <Button
            variante="fantasma"
            tamanho="sm"
            icone="receipt_long"
            onClick={() => verLancamentos(categoria)}
            title="Ver os lançamentos desta categoria no mês"
          />
          {editandoId !== categoria.id && sugestao > 0 && (
            <Button
              variante="suave"
              tamanho="sm"
              icone="auto_awesome"
              carregando={salvandoId === categoria.id}
              onClick={() => salvarMeta(categoria, sugestao)}
            >
              Usar sugestão
            </Button>
          )}
          {renderAcaoMeta(categoria)}
        </div>
      </Card>
    );
  };

  const stepper = (
    <div className={styles.stepper}>
      <button type="button" className={styles.seta} onClick={() => passoMes(-1)} aria-label="Mês anterior">
        <Icon nome="chevron_left" tamanho={20} />
      </button>
      <span className={styles.rotuloMes}>{rotuloMes}</span>
      <button type="button" className={styles.seta} onClick={() => passoMes(1)} aria-label="Próximo mês">
        <Icon nome="chevron_right" tamanho={20} />
      </button>
    </div>
  );

  return (
    <AppShell
      titulo="Metas"
      subtitulo="Acompanhe o orçamento mensal por categoria e saiba se está dentro do plano."
      acoes={categoriasDespesa.length > 0 ? stepper : undefined}
    >
      {carregandoLista ? (
        <Skeleton tipo="linhas" quantidade={5} altura={84} />
      ) : categoriasDespesa.length === 0 ? (
        <EmptyState
          icone="flag"
          titulo="Nenhuma categoria de despesa ainda"
          descricao="As metas são definidas por categoria: primeiro crie suas categorias de despesa (mercado, moradia, lazer...) e depois volte aqui para planejar o orçamento de cada uma."
          acao={
            <Button variante="primaria" icone="category" onClick={() => navigate('/categorias')}>
              Criar categorias
            </Button>
          }
        />
      ) : (
        <div className={styles.conteudo}>
          {despesasComMeta.length > 0 && (
            <div className={styles.stats}>
              <StatCard
                rotulo="Orçamento total"
                valor={valorParaTexto(orcamentoTotal, true)}
                icone="flag"
                tom="primario"
                dica={`${despesasComMeta.length} ${despesasComMeta.length === 1 ? 'categoria com meta' : 'categorias com meta'}`}
              />
              <StatCard
                rotulo="Gasto até agora"
                valor={valorParaTexto(gastoOrcado, true)}
                icone="shopping_cart"
                delta={`${pctOrcamento}% do orçamento`}
                deltaTom={pctOrcamento > 100 ? 'ruim' : pctOrcamento >= 80 ? 'neutro' : 'bom'}
              />
              <StatCard
                rotulo="Disponível"
                valor={valorParaTexto(disponivel, true)}
                icone="account_balance_wallet"
                tom={disponivel < 0 ? 'negativo' : 'positivo'}
                dica={disponivel < 0 ? 'orçamento estourado' : 'até o fim do mês'}
              />
              {ehMesFuturo ? (
                <StatCard rotulo="Ritmo" valor="—" icone="speed" dica="este mês ainda não começou" />
              ) : (
                <StatCard
                  rotulo="Ritmo"
                  valor={valorParaTexto(projecao, true)}
                  icone="speed"
                  delta={
                    ehMesAtual
                      ? projecao <= orcamentoTotal
                        ? 'Dentro do orçamento'
                        : 'Acima do orçamento'
                      : gastoOrcado <= orcamentoTotal
                        ? 'Fechou dentro do orçamento'
                        : 'Fechou acima do orçamento'
                  }
                  deltaTom={(ehMesAtual ? projecao : gastoOrcado) <= orcamentoTotal ? 'bom' : 'ruim'}
                  dica={ehMesAtual ? 'no ritmo atual, até o fim do mês' : 'total realizado no mês'}
                />
              )}
            </div>
          )}

          {despesasComMeta.length > 0 && (
            <section className={styles.secao}>
              <h2 className={styles.secaoTitulo}>Orçamento por categoria</h2>
              <p className={styles.secaoDescricao}>
                Quanto já foi gasto em {rotuloMes} em relação à meta de cada categoria.
              </p>
              <div className={styles.lista}>
                {[...despesasComMeta]
                  .sort((a, b) => realizadoDe(b, gastoMes) / numero(b.valorMeta) - realizadoDe(a, gastoMes) / numero(a.valorMeta))
                  .map((categoria) => renderLinhaComMeta(categoria, 'D'))}
              </div>
            </section>
          )}

          {receitasComMeta.length > 0 && (
            <section className={styles.secao}>
              <h2 className={styles.secaoTitulo}>Receitas com meta</h2>
              <p className={styles.secaoDescricao}>Quanto já entrou em {rotuloMes} em relação à meta de receita.</p>
              <div className={styles.lista}>
                {receitasComMeta.map((categoria) => renderLinhaComMeta(categoria, 'R'))}
              </div>
            </section>
          )}

          {despesasSemMeta.length > 0 && (
            <section className={styles.secao}>
              <h2 className={styles.secaoTitulo}>Sem meta ainda</h2>
              <p className={styles.secaoDescricao}>
                Defina uma meta para acompanhar essas categorias. Sugerimos a média de gasto dos últimos 3 meses.
              </p>
              <div className={styles.lista}>
                {[...despesasSemMeta]
                  .sort((a, b) => realizadoDe(b, gastoMes) - realizadoDe(a, gastoMes))
                  .map(renderLinhaSemMeta)}
              </div>
            </section>
          )}
        </div>
      )}
    </AppShell>
  );
}
